import React, { useState } from 'react';
import { getYouTubeEmbedUrl, getYouTubeThumbnailUrl, isYouTube } from '../../utils/mediaEmbed';

/**
 * YouTubeEmbed — Composant click-to-play RGPD-friendly.
 *
 * Rendu :
 *  - Avant clic : miniature (façade) + bouton play. Aucun script/cookie YouTube chargé.
 *  - Après clic : iframe youtube-nocookie.com autoplay, responsive 16:9.
 *
 * Si l'URL n'est pas YouTube → retourne un simple lien (jamais d'iframe non validée).
 * Si l'URL est vide/malformée → retourne null (le parent gère l'affichage conditionnel).
 */
const YouTubeEmbed = ({ url, title = 'Vidéo de présentation', testId = 'public-video' }) => {
  const [loaded, setLoaded] = useState(false);

  if (!url) return null;

  // URL non-YouTube → lien simple (jamais d'iframe arbitraire)
  if (!isYouTube(url)) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testId}-link`}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition text-sm font-medium"
      >
        <i className="bi bi-play-btn"></i>
        Voir la vidéo
      </a>
    );
  }

  const thumb = getYouTubeThumbnailUrl(url, 'hqdefault');
  const embed = getYouTubeEmbedUrl(url, { autoplay: true });

  if (loaded) {
    return (
      <div
        className="relative w-full aspect-video rounded-xl overflow-hidden bg-black shadow-lg"
        data-testid={`${testId}-iframe-wrapper`}
      >
        <iframe
          src={embed}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          className="absolute inset-0 w-full h-full border-0"
          data-testid={`${testId}-iframe`}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setLoaded(true)}
      aria-label="Lire la vidéo"
      data-testid={`${testId}-play-btn`}
      className="group relative w-full aspect-video rounded-xl overflow-hidden bg-slate-900 shadow-lg cursor-pointer focus:outline-none focus:ring-4 focus:ring-blue-500/40"
    >
      {thumb && (
        <img
          src={thumb}
          alt={title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition group-hover:scale-105"
          onError={(e) => {
            // Fallback : cacher l'image si la miniature n'existe pas → fond noir
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      {/* Overlay sombre pour lisibilité du bouton play */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20 transition group-hover:from-black/60" />
      {/* Bouton play centré */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex items-center justify-center w-20 h-20 rounded-full bg-red-600/95 shadow-2xl transition group-hover:scale-110 group-hover:bg-red-600">
          <i className="bi bi-play-fill text-white text-4xl ml-1"></i>
        </div>
      </div>
      {/* Badge YouTube discret */}
      <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-white/90 text-xs font-semibold">
        <i className="bi bi-youtube text-red-500 text-base"></i>
        <span>YouTube</span>
      </div>
    </button>
  );
};

export default YouTubeEmbed;
